import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useSelector, useDispatch } from "react-redux";

// If you use redux fetchData, keep it. Otherwise remove these two lines.
import { fetchData } from "actions/actions";

// Adjust this import to your project path
import { seminarDetails } from "./SeminarDetails";

export default function SeminarPage() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);
  const dispatch = useDispatch();

  useEffect(() => {
    // Safe even if you don't need it; remove if unused in your project.
    dispatch(fetchData());
  }, [dispatch]);

  // Modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(null);

  const openModalWithImage = (imageSrc) => {
    setSelectedImage(imageSrc);
    onOpen();
  };

  // Theme
  const accent = "#DC242C";
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.04)");
  const heroBg = useColorModeValue(
    "linear-gradient(135deg, rgba(49,83,105,0.10), rgba(220,36,44,0.10))",
    "linear-gradient(135deg, rgba(49,83,105,0.16), rgba(220,36,44,0.14))"
  );

  // ---------------------------
  // Filtering + sorting controls
  // ---------------------------
  const [query, setQuery] = useState("");
  const [fromDate, setFromDate] = useState(""); // YYYY-MM-DD
  const [toDate, setToDate] = useState("");     // YYYY-MM-DD
  const [sortMode, setSortMode] = useState("newest"); // newest | oldest

  const PAGE_SIZE = isMobile ? 6 : 9;
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    // reset visible when layout changes
    setVisible(PAGE_SIZE);
  }, [PAGE_SIZE]);

  // Day-only helpers
  const toDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const parseSeminarDate = (dateStr) => {
    // Best if dateStr is ISO "YYYY-MM-DD".
    // If your format is "30 JAN 2026", tell me and I'll adjust parsing safely.
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    return toDay(d);
  };

  const isUpcoming = (dateStr) => {
    const sd = parseSeminarDate(dateStr);
    if (!sd) return false;
    const today = toDay(new Date());
    return sd >= today;
  };

  const matchesQuery = (s, q) => {
    const needle = (q || "").trim().toLowerCase();
    if (!needle) return true;

    // Search across common fields (safe if undefined)
    const hay = [
      s.title,
      s.address,
      s.speaker,
      s.affiliation,
      s.host,
      s.abstract,
      s.description,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return hay.includes(needle);
  };

  const seminarsFiltered = useMemo(() => {
    const arr = Array.isArray(seminarDetails) ? [...seminarDetails] : [];

    // keyword filter
    let out = arr.filter((s) => matchesQuery(s, query));

    // date range filter
    const from = fromDate ? toDay(fromDate) : null;
    const to = toDate ? toDay(toDate) : null;

    if (from || to) {
      out = out.filter((s) => {
        const sd = parseSeminarDate(s.date);
        if (!sd) return false; // remove invalid date rows (or set true if you want to keep them)
        if (from && sd < from) return false;
        if (to && sd > to) return false;
        return true;
      });
    }

    // sort
    out.sort((a, b) => {
      const da = parseSeminarDate(a.date)?.getTime() ?? 0;
      const db = parseSeminarDate(b.date)?.getTime() ?? 0;
      return sortMode === "oldest" ? da - db : db - da;
    });

    return out;
  }, [query, fromDate, toDate, sortMode]);

  // Reset visible when filters change (so you don’t end up on “page 3” after searching)
  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [query, fromDate, toDate, sortMode, PAGE_SIZE]);

  const SeminarCard = ({ content }) => {
    const upcoming = isUpcoming(content.date);

    return (
      <Box
        role="button"
        onClick={() => openModalWithImage(content.image)}
        bg={cardBg}
        border="1px solid"
        borderColor={border}
        borderRadius="20px"
        p={6}
        textAlign="left"
        boxShadow={useColorModeValue("0 10px 24px rgba(0,0,0,0.07)", "none")}
        transition="all 180ms ease"
        _hover={{ transform: "translateY(-2px)" }}
      >
        <Text fontWeight="900" color={titleColor} fontSize="lg" lineHeight={1.2}>
          {content.title}
        </Text>

        <HStack mt={3} spacing={2} flexWrap="wrap">
          <Tag
            size="md"
            borderRadius="full"
            bg={useColorModeValue("rgba(49,83,105,0.08)", "rgba(255,255,255,0.08)")}
            color={titleColor}
            fontWeight="900"
          >
            {content.date}
          </Tag>

          {upcoming ? (
            <Tag size="md" borderRadius="full" bg={accent} color="white" fontWeight="900">
              UPCOMING
            </Tag>
          ) : null}
        </HStack>

        {content.address ? (
          <Text mt={3} color={subColor} fontSize="md">
            {content.address}
          </Text>
        ) : null}

        <Text mt={4} fontSize="sm" color={useColorModeValue("blue.600", "blue.300")} fontStyle="italic">
          Click for more information
        </Text>
      </Box>
    );
  };

  return (
    <Box>
      {/* HERO */}
      <Box pt={{ base: "140px", md: "140px" }} pb={{ base: 10, md: 12 }} bg={heroBg} borderBottom="1px solid" borderColor={border}>
        <Container maxW="1200px">
          <Stack spacing={3}>
            <HStack spacing={2} flexWrap="wrap">
              <Tag
                size="lg"
                borderRadius="full"
                bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
                border="1px solid"
                borderColor={border}
                fontWeight="800"
              >
                University of Ottawa
              </Tag>
              <Tag
                size="lg"
                borderRadius="full"
                bg={useColorModeValue("rgba(220,36,44,0.10)", "rgba(220,36,44,0.20)")}
                color={accent}
                fontWeight="900"
              >
                SITE-CAV
              </Tag>
            </HStack>

            <Heading color={titleColor} fontSize={{ base: "3xl", md: "4xl" }} letterSpacing="tight">
              SITE-CAV Seminars
            </Heading>

            <Text color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="90ch">
              The SITE-CAV seminar series explores research, technology commercialization, and entrepreneurship in secure and
              trustworthy connected and autonomous vehicles—bringing together academia, industry, and policymakers.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* CONTENT */}
      <Box py={{ base: 10, md: 12 }}>
        <Container maxW="1200px">
          {/* Filters */}
          <Box
            border="1px solid"
            borderColor={border}
            bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
            borderRadius="22px"
            p={{ base: 4, md: 5 }}
            mb={6}
          >
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} alignItems="center">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color={useColorModeValue("gray.500", "gray.400")} />
                </InputLeftElement>
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search title, speaker, affiliation, location…"
                  borderRadius="14px"
                />
              </InputGroup>

              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                borderRadius="14px"
              />

              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                borderRadius="14px"
              />

              <Select value={sortMode} onChange={(e) => setSortMode(e.target.value)} borderRadius="14px">
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </Select>
            </SimpleGrid>

            <HStack mt={4} spacing={3} flexWrap="wrap">
              <Tag borderRadius="full" fontWeight="900">
                Showing {Math.min(visible, seminarsFiltered.length)} / {seminarsFiltered.length}
              </Tag>

              {(query || fromDate || toDate) ? (
                <Button
                  size="sm"
                  variant="ghost"
                  fontWeight="900"
                  onClick={() => {
                    setQuery("");
                    setFromDate("");
                    setToDate("");
                    setVisible(PAGE_SIZE);
                  }}
                >
                  Clear filters
                </Button>
              ) : null}
            </HStack>
          </Box>

          {/* Grid */}
          {seminarsFiltered.length === 0 ? (
            <Box border="1px solid" borderColor={border} borderRadius="20px" p={10} textAlign="center">
              <Text fontWeight="900" color={titleColor}>No seminars match your filters.</Text>
              <Text color={subColor} mt={2}>Try clearing filters or changing the date range.</Text>
            </Box>
          ) : (
            <>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                {seminarsFiltered.slice(0, visible).map((s, idx) => (
                  <SeminarCard key={idx} content={s} />
                ))}
              </SimpleGrid>

              {visible < seminarsFiltered.length ? (
                <Flex justify="center" mt={8}>
                  <Button
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    bg={accent}
                    color="white"
                    fontWeight="900"
                    borderRadius="16px"
                    _hover={{ filter: "brightness(0.95)" }}
                  >
                    Load more
                  </Button>
                </Flex>
              ) : null}
            </>
          )}

          <Divider mt={{ base: 10, md: 12 }} borderColor={border} />
        </Container>
      </Box>

      {/* MODAL */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="3xl" allowPinchZoom>
        <ModalOverlay />
        <ModalContent>
          {selectedImage ? (
            <>
              <ModalHeader>Seminar Details</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Image src={selectedImage} alt="Seminar" width="100%" objectFit="contain" />
              </ModalBody>
              <ModalFooter>
                <Button bg={accent} color="white" fontWeight="900" onClick={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </>
          ) : null}
        </ModalContent>
      </Modal>
    </Box>
  );
}
